import {
  MailPlusIcon,
  MoreHorizontalIcon,
  PauseIcon,
  PlayIcon,
  ShieldCheckIcon,
  UserPlusIcon,
  UsersIcon,
} from 'lucide-react'
import { ApiError, errorMessage } from '@/api/client'
import { ApiErrorText } from '@/components/UpgradePrompt'
import { useState, type FormEvent } from 'react'

import {
  useAccount,
  useInviteUser,
  useMe,
  useUpdateUserRole,
  useUserLifecycle,
  useUsers,
  useZones,
} from '@/api/queries'
import type { AccountUser, MembershipRole } from '@/api/types'
import { choosesZones, zoneIdsFor } from './invite-zones'
import { PageBody, PageHeader } from '@/components/AppShell'
import { LifecycleBadge, RoleBadge } from '@/components/badges'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatAbsolute, formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useReadOnly } from '@/staff/hooks'
import {
  BILLING_MEMBER_LIMIT,
  billingMembers,
  billingSlotsFull,
  offersBillingFlag,
  ROLE_HELP,
  seatMembers,
  userErrorText,
} from '@/lib/access'

function RolePicker({
  value,
  onChange,
  disabled,
  billingFull = false,
  billingFlag,
  onBillingFlag,
}: {
  value: MembershipRole
  onChange: (role: MembershipRole) => void
  disabled?: boolean
  /** Three BILLING members already: the option is offered but cannot be picked. */
  billingFull?: boolean
  billingFlag?: boolean
  onBillingFlag?: (next: boolean) => void
}) {
  return (
    <div className="space-y-2">
      <div className="inline-flex rounded-lg bg-muted p-1" role="group" aria-label="Role">
        {(['USER', 'ADMIN', 'BILLING'] as const).map((role) => (
          <button
            key={role}
            type="button"
            data-testid={`role-${role}`}
            title={role === 'BILLING' && billingFull ? `At most ${BILLING_MEMBER_LIMIT} billing members.` : undefined}
            disabled={disabled === true || (role === 'BILLING' && billingFull && value !== 'BILLING')}
            onClick={() => {
              onChange(role)
            }}
            className={cn(
              'cursor-pointer rounded-md px-3 py-1 font-mono text-[0.8125rem] transition-colors',
              value === role
                ? 'bg-card text-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {role}
          </button>
        ))}
      </div>
      <p className="text-xs text-pretty text-muted-foreground">{ROLE_HELP[value]}</p>
      {offersBillingFlag(value) && onBillingFlag !== undefined ? (
        <label className="flex cursor-pointer items-start gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="billing-access"
            className="mt-0.5 size-4 accent-primary"
            disabled={disabled === true}
            checked={billingFlag === true}
            onChange={(event) => {
              onBillingFlag(event.target.checked)
            }}
          />
          <span>
            Billing access
            <span className="block text-xs text-muted-foreground">
              Lets this user see and manage the plan, invoices, cards and police balance. They still count as a seat.
            </span>
          </span>
        </label>
      ) : null}
    </div>
  )
}

/** The user endpoints' billing refusals in words; everything else falls through to the quota prompt. */
function UserErrorText({ error }: { error: unknown }) {
  const code = error instanceof ApiError ? error.code : undefined
  if (code === 'billing_member_limit' || code === 'billing_has_no_zones') {
    return <>{userErrorText(code, errorMessage(error))}</>
  }
  return <ApiErrorText error={error} />
}

function InviteUserDialog({ atLimit, billingFull }: { atLimit: boolean; billingFull: boolean }) {
  const invite = useInviteUser()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<MembershipRole>('USER')
  const [billingFlag, setBillingFlag] = useState(false)
  const [zoneIds, setZoneIds] = useState<string[]>([])
  const showZones = choosesZones(role)
  const zones = useZones('active')

  function submit(event: FormEvent) {
    event.preventDefault()
    invite.mutate(
      {
        email: email.trim(),
        name: name.trim(),
        role,
        zone_ids: zoneIdsFor(role, zoneIds),
        ...(offersBillingFlag(role) && billingFlag ? { billing: true } : {}),
      },
      {
        onSuccess: () => {
          setOpen(false)
          setEmail('')
          setName('')
          setRole('USER')
          setBillingFlag(false)
          setZoneIds([])
        },
      },
    )
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) invite.reset()
      }}
    >
      {/* BI6: a BILLING member is free and never in the member limit, so a full account can
          still invite one; only the seat roles are refused in the dialog. */}
      <Button
        disabled={atLimit && billingFull}
        title={atLimit ? 'This account is at its member limit; only a billing member can be invited.' : undefined}
        onClick={() => {
          setOpen(true)
        }}
      >
        <UserPlusIcon className="size-4" />
        Invite user
      </Button>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Invite someone to this account</DialogTitle>
            <DialogDescription>
              They receive an invitation mail and set their own password. A new USER reaches only
              the zones you pick here.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-5">
            <div className="space-y-1.5">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value)
                }}
                placeholder="teammate@example.com"
                className="font-mono"
                autoComplete="off"
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-name">Name</Label>
              <Input
                id="invite-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value)
                }}
                placeholder="Ada Lovelace"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <RolePicker
                value={role}
                onChange={setRole}
                disabled={invite.isPending}
                billingFull={billingFull}
                billingFlag={billingFlag}
                onBillingFlag={setBillingFlag}
              />
            </div>
            {showZones ? (
              <div className="space-y-1.5">
                <Label>Zones</Label>
                <p className="text-xs text-muted-foreground">
                  Optional. They reach only what you tick here — you can change it later from the
                  zone&rsquo;s members list.
                </p>
                {zones.isPending ? (
                  <p className="text-sm text-muted-foreground">Loading zones&hellip;</p>
                ) : zones.data && zones.data.length > 0 ? (
                  <div
                    role="group"
                    aria-label="Zones"
                    className="max-h-44 space-y-1 overflow-y-auto rounded-md border border-border p-2"
                  >
                    {zones.data.map((zone) => (
                      <label
                        key={zone.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent"
                      >
                        <input
                          type="checkbox"
                          className="size-4 accent-primary"
                          disabled={invite.isPending}
                          checked={zoneIds.includes(zone.id)}
                          onChange={(event) => {
                            setZoneIds((current) =>
                              event.target.checked
                                ? [...current, zone.id]
                                : current.filter((id) => id !== zone.id),
                            )
                          }}
                        />
                        <span className="font-mono">{zone.name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    This account has no active zones yet.
                  </p>
                )}
              </div>
            ) : role === 'BILLING' ? (
              <p className="text-sm text-muted-foreground">
                A billing member has no zone access, so there are no zones to choose. They are free:
                they never count as a seat.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                ADMINs reach every zone in this account, so there are no zones to choose.
              </p>
            )}
            {atLimit && role !== 'BILLING' ? (
              <p className="text-sm text-destructive">
                This account is at its member limit. A billing member is free and can still be
                invited.
              </p>
            ) : null}
            {invite.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not send the invitation</AlertTitle>
                <AlertDescription><UserErrorText error={invite.error} /></AlertDescription>
              </Alert>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false)
              }}
              disabled={invite.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                invite.isPending ||
                email.trim().length === 0 ||
                name.trim().length === 0 ||
                (atLimit && role !== 'BILLING')
              }
            >
              <MailPlusIcon className="size-4" />
              {invite.isPending ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The two mutations live on the row but their *errors* do not: the row is a
 * dropdown that closes on select, so an unreported failure is indistinguishable
 * from success — a 409 (last administrator, archived user) would leave the table
 * unchanged and say nothing at all. `onFailed`/`onStarted` hand the outcome to
 * the page, which owns the one alert.
 */
function UserRowActions({
  user,
  isSelf,
  billingFull,
  onStarted,
  onFailed,
}: {
  user: AccountUser
  isSelf: boolean
  billingFull: boolean
  onStarted: () => void
  onFailed: (error: unknown) => void
}) {
  const changeRole = useUpdateUserRole()
  const lifecycle = useUserLifecycle()
  const active = user.user_state === 'ACTIVE'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${user.email}`}>
          <MoreHorizontalIcon className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[15rem]">
        <DropdownMenuLabel>Role</DropdownMenuLabel>
        {(['ADMIN', 'USER', 'BILLING'] as const).map((role) => (
          <DropdownMenuItem
            key={role}
            disabled={
              role === user.role ||
              changeRole.isPending ||
              isSelf ||
              (role === 'BILLING' && billingFull)
            }
            onSelect={() => {
              onStarted()
              changeRole.mutate({ userId: user.id, role }, { onError: onFailed })
            }}
          >
            <ShieldCheckIcon
              className={cn('size-3.5', role === user.role ? 'opacity-100' : 'opacity-0')}
            />
            <span className="font-mono">{role}</span>
          </DropdownMenuItem>
        ))}
        {user.role === 'USER' ? (
          <DropdownMenuItem
            data-testid="toggle-billing-access"
            disabled={changeRole.isPending}
            onSelect={() => {
              onStarted()
              changeRole.mutate({ userId: user.id, role: 'USER', billing: !user.billing }, { onError: onFailed })
            }}
          >
            <ShieldCheckIcon className={cn('size-3.5', user.billing ? 'opacity-100' : 'opacity-0')} />
            Billing access
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          disabled={lifecycle.isPending || isSelf}
          className={active ? 'text-destructive [&_svg]:text-destructive' : undefined}
          onSelect={() => {
            onStarted()
            lifecycle.mutate({ userId: user.id, activate: !active }, { onError: onFailed })
          }}
        >
          {active ? <PauseIcon className="size-3.5" /> : <PlayIcon className="size-3.5" />}
          {active ? 'Deactivate user' : 'Activate user'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function UsersPage() {
  const me = useMe()
  const readOnly = useReadOnly()
  const isAdmin = me.data?.role === 'ADMIN'
  // A BILLING member reads the list (BI5) and changes nothing: every write is ADMIN.
  const canRead = isAdmin || me.data?.role === 'BILLING'
  const account = useAccount()
  const users = useUsers(canRead)
  const [actionError, setActionError] = useState<unknown>(undefined)

  if (me.isSuccess && !canRead) {
    return (
      <>
        <PageHeader eyebrow="Users" title="Users" />
        <PageBody>
          <EmptyState
            icon={ShieldCheckIcon}
            title="ADMIN only"
            description="Inviting users, changing roles and deactivating accounts are ADMIN operations."
          />
        </PageBody>
      </>
    )
  }

  const rows = users.data ?? []
  // BILLING members are free (BI6): they are neither a seat nor counted against the plan.
  const activeCount = seatMembers(rows)
  const billingCount = billingMembers(rows)
  const billingFull = billingSlotsFull(rows)
  const maxMembers = account.data?.limits.max_members
  const atLimit = maxMembers !== undefined && activeCount >= maxMembers
  // `users` is disabled until `me` resolves, and a disabled react-query v5 query
  // reports isLoading === false — so isLoading alone renders a confident "you are
  // the only user here" in the gap before the real list arrives.
  const loadingUsers = me.isPending || users.isPending

  return (
    <>
      <PageHeader
        eyebrow="Users"
        title="Users on this account"
        description="An ADMIN reaches every zone in the account. A USER reaches only the zones they have been granted — nothing until then."
        actions={readOnly || !isAdmin ? undefined : <InviteUserDialog atLimit={atLimit} billingFull={billingFull} />}
      />

      <PageBody>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-muted-foreground">
            {loadingUsers ? 'Loading…' : `${activeCount} active`}
          </p>
          {maxMembers === undefined ? null : (
            <Badge variant={atLimit ? 'destructive' : 'outline'} className="font-mono tabular">
              {activeCount} / {maxMembers} members
            </Badge>
          )}
          <Badge variant={billingFull ? 'destructive' : 'outline'} className="font-mono tabular" data-testid="billing-count">
            {billingCount} / {BILLING_MEMBER_LIMIT} billing
          </Badge>
        </div>

        {atLimit ? (
          <Alert variant="warning">
            <AlertTitle>This account is at its member limit.</AlertTitle>
            <AlertDescription>
              The limit comes from the plan, or from an override negotiated on this account.
              Deactivating a user frees a seat.
            </AlertDescription>
          </Alert>
        ) : null}

        {actionError === undefined ? null : (
          <Alert variant="destructive">
            <AlertTitle>That change did not go through</AlertTitle>
            <AlertDescription><UserErrorText error={actionError} /></AlertDescription>
          </Alert>
        )}

        {users.isError ? (
          <ErrorState
            error={users.error}
            title="Could not load users"
            onRetry={() => {
              void users.refetch()
            }}
          />
        ) : loadingUsers ? (
          <Card className="overflow-hidden">
            <TableSkeleton rows={4} columns={5} />
          </Card>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title="You are the only user here"
            description="Invite a teammate and give them USER if they should only see the zones you grant them, or ADMIN if they should administer the account with you."
          />
        ) : (
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((user) => {
                  const isSelf = user.id === me.data?.user.id
                  return (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{user.name}</span>
                          {isSelf ? (
                            <Badge variant="outline" className="text-[0.625rem]">
                              you
                            </Badge>
                          ) : null}
                          {user.email_verified ? null : (
                            <Badge variant="secondary" className="text-[0.625rem]">
                              invited
                            </Badge>
                          )}
                        </div>
                        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      </TableCell>
                      <TableCell>
                        <RoleBadge role={user.role} />
                        {user.role === 'USER' && user.billing ? (
                          <Badge variant="outline" className="ml-1.5 text-[0.625rem]" data-testid="billing-flag-badge">
                            billing
                          </Badge>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <LifecycleBadge state={user.user_state} />
                      </TableCell>
                      <TableCell
                        className="text-sm whitespace-nowrap text-muted-foreground"
                        title={formatAbsolute(user.created_at)}
                      >
                        {formatRelative(user.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        {readOnly || !isAdmin ? null : (
                          <UserRowActions
                            user={user}
                            isSelf={isSelf}
                            billingFull={billingFull}
                            onStarted={() => {
                              setActionError(undefined)
                            }}
                            onFailed={setActionError}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        <p className="text-xs text-muted-foreground">
          {isAdmin ? '' : 'Read-only: only administrators change users. '}
          You cannot change your own role or deactivate yourself — that is the one way to lock
          an account out of its own administration.
        </p>
      </PageBody>
    </>
  )
}
